import { createContext } from 'react'
import type { FlashcardCard, FlashcardCategory } from '@services/firebase/flashcards'

export interface FlashcardPromptOption {
  readonly audio?: string | null
  readonly id: string
  readonly image?: string
  readonly isCorrect: boolean
  readonly label?: string
}

export interface FlashcardPromptPair {
  readonly id: string
  readonly leftLabel: string
  readonly rightLabel: string
}

export interface FlashcardPrompt {
  readonly answerText?: string
  readonly answerTranslation?: string
  readonly audio?: string | null
  readonly id: string
  readonly image?: string
  readonly matchPairs?: FlashcardPromptPair[]
  readonly options?: FlashcardPromptOption[]
  readonly promptLabel?: string
  readonly question: string
  readonly shuffledLetters?: string[]
  readonly template: 1 | 2 | 3 | 4 | 5
}

export interface FlashcardGameSession {
  readonly activeIndex: number
  readonly correctCount: number
  readonly incorrectCount: number
  readonly level: 'basic' | 'advanced' | 'pro'
  readonly prompts: FlashcardPrompt[]
  readonly status: 'idle' | 'playing' | 'finished'
}

export interface FlashcardsFeatureState {
  readonly activeCategory: FlashcardCategory | null
  readonly cardsByCategoryId: Record<string, FlashcardCard[]>
  readonly categoriesLoaded: boolean
  readonly categories: FlashcardCategory[]
  readonly errorMessage: string | null
  readonly gameSession: FlashcardGameSession | null
  readonly loading: {
    readonly cards: boolean
    readonly categories: boolean
  }
  readonly selectedCardIds: string[]
  readonly slideshow: {
    readonly activeIndex: number
    readonly open: boolean
  }
}

export interface FlashcardsStateActions {
  readonly clearErrorMessage: () => void
  readonly clearSelectedCards: () => void
  readonly closeSlideshow: () => void
  readonly finishGameSession: () => void
  readonly setActiveCategory: (category: FlashcardCategory | null) => void
  readonly setCards: (categoryId: string, cards: FlashcardCard[]) => void
  readonly setCardsLoading: (isLoading: boolean) => void
  readonly setCategories: (categories: FlashcardCategory[]) => void
  readonly setCategoriesLoading: (isLoading: boolean) => void
  readonly setErrorMessage: (message: string | null) => void
  readonly setGameActiveIndex: (activeIndex: number) => void
  readonly openSlideshow: (activeIndex?: number) => void
  readonly resetGameSession: () => void
  readonly scoreCurrentPrompt: (result: 'correct' | 'incorrect') => void
  readonly startGameSession: (session: FlashcardGameSession) => void
  readonly toggleSelectedCard: (cardId: string) => void
}

export interface FlashcardsStateContextValue {
  readonly actions: FlashcardsStateActions
  readonly state: FlashcardsFeatureState
}

export const initialFlashcardsState: FlashcardsFeatureState = {
  activeCategory: null,
  cardsByCategoryId: {},
  categoriesLoaded: false,
  categories: [],
  errorMessage: null,
  gameSession: null,
  loading: {
    cards: false,
    categories: false,
  },
  selectedCardIds: [],
  slideshow: {
    activeIndex: 0,
    open: false,
  },
}

export const FlashcardsStateContext =
  createContext<FlashcardsStateContextValue | null>(null)

